/**
 * Display formatting helpers shared across pages.
 */

/**
 * Format a contract price stored in INR crore exactly as recorded, e.g. 16.35 -> "₹16.35 Cr".
 *
 * Salaries are never rounded for display: at least two decimals are always shown
 * (contracts are quoted to the paisa-level 0.05 Cr step), and any further real
 * digits are kept. Only floating-point noise beyond 4 decimals (from summing) is
 * dropped. Returns null when the price is unknown so callers can decide what to render.
 */
export const formatCrores = (price: number | null | undefined): string | null => {
  if (price === null || price === undefined || Number.isNaN(price)) return null;
  const text = price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return `₹${text} Cr`;
};

/**
 * Sum the known prices of a group of players.
 * `unpriced` counts players whose price is unknown so the UI can say so.
 */
export const sumPrices = (
  players: Array<{ price?: number | null }>
): { total: number; unpriced: number } =>
  players.reduce(
    (acc, p) => {
      if (p.price === null || p.price === undefined || Number.isNaN(p.price)) {
        acc.unpriced += 1;
      } else {
        acc.total += p.price;
      }
      return acc;
    },
    { total: 0, unpriced: 0 }
  );
